import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import CheckoutSummaryCgv from '../CheckoutSummaryCgv/CheckoutSummaryCgv';
import { isFr as isFrFct } from 'services/currentCountry';
import { formatPrice } from 'services/formatter';
import { Cart } from 'types/Cart';

import styles from './CheckoutSummaryTotal.module.scss';

export default function CheckoutSummaryTotal({
  cart,
  isCartPage = false,
}: {
  cart: Cart;
  isCartPage?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const freeLabel = t('LABEL_FREE');
  const isFr = isFrFct();

  const listItems: ListItemsType[] = [
    {
      label: t('LABEL_SUBTOTAL'),
      data: cart.subTotalPrice,
      defaultValue: freeLabel,
    },
    {
      className: styles.discount,
      label: t('LABEL_DISCOUNT'),
      data: -cart.discount,
    },
    {
      label: t('LABEL_CLEARANCE'),
      data: cart.clearance,
      isHidden: isCartPage,
    },
    {
      className: styles.delivery,
      label: t('LABEL_DELIVERY'),
      data: isCartPage && isFr ? cart.shippingFees : cart.totalShippingFees,
      defaultValue: freeLabel,
      isHidden: isCartPage && isFr && cart.shippingFees !== 0,
    },
    {
      label: t('LABEL_AGAINST_REFUND'),
      data: cart.againstRefund,
      isHidden: isCartPage,
    },
    {
      className: styles.total,
      label: t('LABEL_TOTAL'),
      data: isCartPage && isFr ? cart.subtotalWithDiscount : cart.totalPrice,
      defaultValue: freeLabel,
    },
  ];

  function getFormattedPrice(price: number, defaultValue: string | undefined) {
    return price !== 0 ? formatPrice(price.toFixed(2)) : defaultValue;
  }

  return (
    <div
      className={classNames(styles.container, isCartPage && styles.cartPage)}
    >
      <p className={styles.title}>{t('LABEL_ORDER')}</p>
      <div>
        {listItems.map(({ className, data, label, defaultValue, isHidden }) => {
          const value =
            data !== undefined && getFormattedPrice(data, defaultValue);

          if (!isHidden && !!value) {
            return (
              <div key={label} className={classNames(styles.item, className)}>
                <span className={styles.label}>{label}</span>
                <span className={styles.value}>{value}</span>
              </div>
            );
          }
        })}
      </div>
      <CheckoutSummaryCgv classes={{ wrapper: styles.cgv }} />
    </div>
  );
}

type ListItemsType = {
  className?: string;
  data?: number;
  defaultValue?: string;
  label: string;
  isHidden?: boolean;
};
